import React from "react";
import {
  BarChart3,
  CalendarClock,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Mic,
  Moon,
  Phone,
  PhoneCall,
  ScrollText,
  Shield,
  Smartphone,
  Sun,
  TrendingUp,
  User,
  Users,
  Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LoginOtpPanels from "@/components/auth/LoginOtpPanels";
import RememberMeField from "@/components/auth/RememberMeField";
import RememberMeModal from "@/components/auth/RememberMeModal";
import { useLoginOtpFlow } from "@/hooks/useLoginOtpFlow";
import { API } from "@/utils/constants";
import "./login2.css";
import "./login3.css";

const FEATURES = [
  { id: "gsm", label: "GSM", Icon: Smartphone, tone: "gsm" },
  { id: "voip", label: "VoIP", Icon: PhoneCall, tone: "voip" },
  { id: "report", label: "Raporlama", Icon: BarChart3, tone: "report" },
  { id: "recording", label: "Call Recording", Icon: Mic, tone: "recording" },
  { id: "cdr", label: "CDR", Icon: ScrollText, tone: "cdr" },
  { id: "crm", label: "CRM", Icon: Users, tone: "crm" },
  { id: "audio", label: "Ses & Transkript", Icon: Volume2, tone: "audio" },
  { id: "schedule", label: "Mesai Yönetimi", Icon: CalendarClock, tone: "schedule" },
  { id: "agent", label: "Agent Performans", Icon: TrendingUp, tone: "agent" },
];

const FeatureHoverFx = ({ type }) => {
  if (type === "gsm") {
    return (
      <span className="login3-fx login3-fx--gsm" aria-hidden="true">
        <span /><span /><span /><span />
      </span>
    );
  }
  if (type === "voip") {
    return (
      <span className="login3-fx login3-fx--voip" aria-hidden="true">
        <span /><span /><span />
      </span>
    );
  }
  if (type === "report") {
    return (
      <span className="login3-fx login3-fx--report" aria-hidden="true">
        <span /><span /><span /><span />
      </span>
    );
  }
  if (type === "recording") {
    return (
      <span className="login3-fx login3-fx--recording" aria-hidden="true">
        <span /><span /><span /><span /><span />
      </span>
    );
  }
  if (type === "cdr") {
    return (
      <span className="login3-fx login3-fx--cdr" aria-hidden="true">
        <span /><span /><span />
      </span>
    );
  }
  if (type === "crm") {
    return (
      <span className="login3-fx login3-fx--crm" aria-hidden="true">
        <span /><span /><span />
      </span>
    );
  }
  if (type === "audio") {
    return (
      <span className="login3-fx login3-fx--audio" aria-hidden="true">
        <span /><span /><span /><span /><span />
      </span>
    );
  }
  if (type === "schedule") {
    return (
      <span className="login3-fx login3-fx--schedule" aria-hidden="true">
        <span className="login3-fx-hand" />
      </span>
    );
  }
  if (type === "agent") {
    return (
      <span className="login3-fx login3-fx--agent" aria-hidden="true">
        <span className="login3-fx-spark" /><span className="login3-fx-spark" /><span className="login3-fx-spark" />
      </span>
    );
  }
  return null;
};

const LoginPage3 = ({ isDark, onToggleTheme, login, finalizeOtpLogin }) => {
  const {
    username,
    setUsername,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    loading,
    rememberChecked,
    rememberDays,
    rememberModalOpen,
    handleRememberCheckedChange,
    handleRememberConfirm,
    handleRememberCancel,
    otpFlow,
    loginLoader,
    handleLogin,
    handleOtpComplete,
    dismissOtpFlow,
  } = useLoginOtpFlow({ login, finalizeOtpLogin, isDark });

  const themeClass = isDark ? "login2-root--dark" : "login2-root--light";

  return (
    <div className={`login2-root ${themeClass}`}>
      <div className="login2-bg" aria-hidden="true">
        <div className="login2-bg-grid" />
        <div className="login2-orb login2-orb--cyan" />
        <div className="login2-orb login2-orb--blue" />
        <div className="login2-orb login2-orb--violet" />
        <div className="login2-scanline" />
      </div>

      <header className="login2-topbar login3-topbar">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onToggleTheme}
          className={isDark ? "text-slate-300 hover:text-white hover:bg-slate-800/80" : "text-slate-600 hover:bg-white/80"}
          aria-label="Tema değiştir"
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      <div className="login2-shell login3-shell">
        <section className="login2-hero login3-hero">
          <div className="login3-hero-stack">
            <div className="login3-hero-banner">
              <div className="login3-brand-mark">
                <div className="login3-brand-logo">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="login3-brand-text">
                  <span className="login3-brand-name">Etelekom</span>
                  <span className="login3-brand-tag">Call Center Platform</span>
                </div>
              </div>

              <span className="login3-hero-divider" aria-hidden="true" />

              <div className="login3-hero-title-wrap">
                <h1 className="login3-hero-headline">
                  Çağrı merkezi <em>kontrol paneli</em>
                </h1>
                <p className="login3-hero-lead">
                  Agent performans ve çağrı merkezi yönetimi
                </p>
              </div>
            </div>

            <div className="login3-feature-grid" aria-label="Platform özellikleri">
              {FEATURES.map(({ id, label, Icon, tone }) => (
                <div key={id} className={`login3-feature-tile login3-feature-tile--${tone}`} title={label}>
                  <FeatureHoverFx type={tone} />
                  <span className="login3-feature-scan" aria-hidden="true" />
                  <span className="login3-feature-ring" aria-hidden="true" />
                  <span className={`login3-feature-icon login3-feature-icon--${tone}`}>
                    <Icon className="login3-feature-icon-svg w-5 h-5" strokeWidth={2.1} />
                  </span>
                  <span className="login3-feature-label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="login2-card-wrap">
          <div className="login2-card">
            <div className="login2-card-glow" aria-hidden="true" />

            <div className="login2-mobile-brand">
              <div className="login3-brand-logo">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="login3-card-brand">Etelekom</p>
                <p className="login2-card-sub">Çağrı Kayıt Yönetim Paneli</p>
              </div>
            </div>

            <div className="hidden sm:block">
              <h2 className="login2-card-title">Hoş geldiniz</h2>
              <p className="login2-card-sub">Etelekom panel hesabınıza giriş yapın</p>
            </div>

            <form onSubmit={handleLogin} className="mt-1">
              <div className="login2-field">
                <label className="login2-label" htmlFor="login3-username">
                  E-posta veya kullanıcı adı
                </label>
                <div className="login2-input-wrap">
                  {username.includes("@") ? (
                    <Mail className="login2-input-icon" />
                  ) : (
                    <User className="login2-input-icon" />
                  )}
                  <input
                    id="login3-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="login2-input"
                    placeholder="ornek@etelekom.net"
                    autoComplete="username"
                    data-testid="login3-username-input"
                  />
                </div>
              </div>

              <div className="login2-field">
                <label className="login2-label" htmlFor="login3-password">
                  Şifre
                </label>
                <div className="login2-input-wrap">
                  <Lock className="login2-input-icon" />
                  <input
                    id="login3-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login2-input"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    data-testid="login3-password-input"
                  />
                  <button
                    type="button"
                    className="login2-input-action"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <RememberMeField
                isDark={isDark}
                checked={rememberChecked}
                rememberDays={rememberDays}
                onCheckedChange={handleRememberCheckedChange}
              />

              <button
                type="submit"
                disabled={loading}
                className="login2-submit"
                data-testid="login3-submit-btn"
              >
                <span className="login2-submit-shine" aria-hidden="true" />
                <span className="relative z-[1] inline-flex items-center justify-center gap-2">
                  {loading ? (
                    <>Giriş yapılıyor…</>
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      Güvenli Giriş
                    </>
                  )}
                </span>
              </button>
            </form>
          </div>
        </section>
      </div>

      <RememberMeModal
        apiBase={API}
        isDark={isDark}
        open={rememberModalOpen}
        onCancel={handleRememberCancel}
        onConfirm={handleRememberConfirm}
      />

      {otpFlow && (
        <LoginOtpPanels
          apiBase={API}
          creds={otpFlow.creds}
          isDark={isDark}
          panelLoader={loginLoader}
          initialChooseChannel={otpFlow.chooseChannel}
          initialChannels={otpFlow.channels}
          initialPhoneMasked={otpFlow.phoneMasked}
          initialOtpState={otpFlow.otpState}
          onComplete={handleOtpComplete}
          onDismiss={dismissOtpFlow}
          initialBlocked={!!otpFlow.blocked}
          initialRetryIn={otpFlow.retryIn || 0}
          rememberDays={otpFlow.rememberDays || 0}
          deviceLabel={otpFlow.deviceLabel || ""}
        />
      )}
    </div>
  );
};

export default LoginPage3;
